/**
 * MED-938 / E3.5 — right-hand contextual sidebar: Program, Your Cohort Manager,
 * Group, Program Modalities, and Resources cards. Each card is self-contained
 * and hides gracefully when its data is absent.
 */
import { Anchor, Avatar, Badge, Box, Card, Group, Progress, Stack, Text, ThemeIcon } from '@mantine/core';
import { BookOpen, Briefcase, File, FileText, Link as LinkIcon, Users } from 'react-feather';
import type {
  DashboardRefreshCohortManager,
  DashboardRefreshGroup,
  DashboardRefreshModalityProgress,
  DashboardRefreshProgram,
  DashboardRefreshResource,
} from '../../../services/api/participantPortal.js';

function modalityLabel(type: string): string {
  switch (type) {
    case 'group_coaching':
      return 'Group Coaching';
    case 'one_on_one':
      return '1:1 Coaching';
    case 'workshop':
      return 'Workshops';
    default:
      return type;
  }
}

function modalityIcon(type: string) {
  switch (type) {
    case 'group_coaching':
      return <Users size={14} />;
    case 'one_on_one':
      return <Users size={14} />;
    case 'workshop':
      return <BookOpen size={14} />;
    default:
      return <BookOpen size={14} />;
  }
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p.charAt(0).toUpperCase())
    .join('');
}

function formatShortDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ProgramCard({ program }: { program: DashboardRefreshProgram }) {
  const total = program.sessions_total;
  const pct = total > 0 ? Math.round((program.sessions_attended / total) * 100) : 0;
  return (
    <Card withBorder radius="md" padding="md">
      <Text fw={600} size="sm" mb={4}>
        {program.name}
      </Text>
      {program.partner_name && (
        <Group gap={6} mb="md">
          <Briefcase size={12} color="#7A766E" />
          <Text size="xs" c="dimmed">
            {program.partner_name}
          </Text>
        </Group>
      )}
      <Group justify="space-between" mb={4}>
        <Text size="xs" c="dimmed">
          Progress
        </Text>
        <Text size="xs" fw={600}>
          {program.sessions_attended}/{total}
        </Text>
      </Group>
      <Progress value={pct} color="medleyGreen" size="sm" radius="xl" mb={program.next_milestone_label ? 'sm' : 0} />
      {program.next_milestone_label && (
        <Text size="xs" c="dimmed">
          Next milestone: {program.next_milestone_label}
        </Text>
      )}
    </Card>
  );
}

function CohortManagerCard({ manager }: { manager: DashboardRefreshCohortManager }) {
  return (
    <Card withBorder radius="md" padding="md">
      <Text fw={600} size="sm" mb="sm">
        Your Cohort Manager
      </Text>
      <Group gap="sm" wrap="nowrap">
        <Avatar size={40} radius="xl" color="medleyGreen" variant="filled">
          {initials(manager.name)}
        </Avatar>
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {manager.name}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            Medley · {manager.role_label}
          </Text>
        </Box>
      </Group>
    </Card>
  );
}

function GroupRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xs" fw={500} ta="right">
        {value}
      </Text>
    </Group>
  );
}

function GroupCard({ group }: { group: DashboardRefreshGroup }) {
  return (
    <Card withBorder radius="md" padding="md">
      <Group gap="xs" mb="sm">
        <Users size={14} />
        <Text fw={600} size="sm">
          {group.name}
        </Text>
      </Group>
      <Stack gap={6}>
        {group.schedule_label && <GroupRow label="Schedule" value={group.schedule_label} />}
        <GroupRow
          label="Members"
          value={`${group.member_count} ${group.member_count === 1 ? 'participant' : 'participants'}`}
        />
        {group.next_session_date && (
          <GroupRow label="Next session" value={formatShortDate(group.next_session_date)} />
        )}
      </Stack>
    </Card>
  );
}

function ProgramModalitiesCard({ modalities }: { modalities: DashboardRefreshModalityProgress[] }) {
  if (modalities.length === 0) return null;
  return (
    <Card withBorder radius="md" padding="md">
      <Group gap="xs" mb="sm">
        <BookOpen size={14} />
        <Text fw={600} size="sm">
          Program Modalities
        </Text>
      </Group>
      <Stack gap="xs">
        {modalities.map(m => (
          <Group key={m.modality_type} justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon size={24} radius="md" variant="light" color="gray">
                {modalityIcon(m.modality_type)}
              </ThemeIcon>
              <Text size="xs" fw={500}>
                {modalityLabel(m.modality_type)}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {m.completed_count}/{m.session_count ?? '?'}
            </Text>
          </Group>
        ))}
      </Stack>
    </Card>
  );
}

function ResourceRow({ resource }: { resource: DashboardRefreshResource }) {
  const isPdf = (resource.mime_type ?? '').includes('pdf');
  const icon =
    resource.resource_type === 'link' ? (
      <LinkIcon size={14} />
    ) : isPdf ? (
      <FileText size={14} />
    ) : (
      <File size={14} />
    );
  const label = (
    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
      <ThemeIcon size={24} radius="md" variant="light" color="gray">
        {icon}
      </ThemeIcon>
      <Text size="xs" fw={500} truncate>
        {resource.title}
      </Text>
    </Group>
  );
  return resource.url ? (
    <Anchor href={resource.url} target="_blank" rel="noopener noreferrer" underline="never">
      {label}
    </Anchor>
  ) : (
    label
  );
}

function ResourcesCard({ resources }: { resources: DashboardRefreshResource[] }) {
  if (resources.length === 0) return null;
  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <File size={14} />
          <Text fw={600} size="sm">
            Resources
          </Text>
        </Group>
        <Badge size="sm" variant="light" color="gray">
          {resources.length}
        </Badge>
      </Group>
      <Stack gap="xs">
        {resources.map(r => (
          <ResourceRow key={r.id} resource={r} />
        ))}
      </Stack>
    </Card>
  );
}

export function DashboardSidebar({
  program,
  cohortManager,
  group,
  modalities,
  resources,
}: {
  program: DashboardRefreshProgram | null;
  cohortManager: DashboardRefreshCohortManager | null;
  group: DashboardRefreshGroup | null;
  modalities: DashboardRefreshModalityProgress[];
  resources: DashboardRefreshResource[];
}) {
  return (
    <Stack gap="md">
      {program && <ProgramCard program={program} />}
      {cohortManager && <CohortManagerCard manager={cohortManager} />}
      {group && <GroupCard group={group} />}
      <ProgramModalitiesCard modalities={modalities} />
      <ResourcesCard resources={resources} />
    </Stack>
  );
}
